import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ConversationList = ({ conversations, activeConversation, onSelectConversation, searchQuery }) => {
  const [filter, setFilter] = useState('all');

  const filterOptions = [
    { value: 'all', label: 'All', icon: 'MessageSquare' },
    { value: 'direct', label: 'Direct', icon: 'User' },
    { value: 'team', label: 'Teams', icon: 'Users' },
    { value: 'city', label: 'Cities', icon: 'Building2' },
    { value: 'mentor', label: 'Mentors', icon: 'GraduationCap' }
  ];

  const filteredConversations = conversations?.filter(conv => {
    const matchesSearch = !searchQuery || 
      conv?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      conv?.lastMessage?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const matchesFilter = filter === 'all' || conv?.type === filter;
    
    return matchesSearch && matchesFilter;
  });

  const formatTime = (timestamp) => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return messageTime?.toLocaleDateString();
  };

  const getTypeIcon = (type) => {
    const iconMap = {
      direct: 'User',
      team: 'Users',
      city: 'Building2',
      mentor: 'GraduationCap'
    };
    return iconMap?.[type] || 'MessageSquare';
  };

  const getOnlineStatus = (isOnline) => (
    <div className={`w-3 h-3 rounded-full ${isOnline ? 'bg-success' : 'bg-muted-foreground'}`} />
  );

  return (
    <div className="flex flex-col h-full">
      {/* Filter Tabs */}
      <div className="p-4 border-b border-border">
        <div className="flex space-x-1 bg-muted rounded-lg p-1">
          {filterOptions?.map((option) => (
            <Button
              key={option?.value}
              variant={filter === option?.value ? "default" : "ghost"}
              size="sm"
              onClick={() => setFilter(option?.value)}
              iconName={option?.icon}
              iconPosition="left"
              iconSize={14}
              className="flex-1 transition-micro"
            >
              {option?.label}
            </Button>
          ))}
        </div>
      </div>
      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center p-6">
            <Icon name="MessageSquare" size={48} className="text-muted-foreground mb-4" />
            <h3 className="font-medium text-foreground mb-2">No conversations found</h3>
            <p className="text-sm text-muted-foreground">
              {searchQuery ? 'Try adjusting your search terms' : 'Start a new conversation to get started'}
            </p>
          </div>
        ) : (
          <div className="space-y-1 p-2">
            {filteredConversations?.map((conversation) => (
              <div
                key={conversation?.id}
                onClick={() => onSelectConversation(conversation)}
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-micro hover:bg-muted ${
                  activeConversation?.id === conversation?.id ? 'bg-primary/10 border border-primary/20' : ''
                }`}
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {conversation?.avatar ? (
                    <Image
                      src={conversation?.avatar}
                      alt={conversation?.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                      <Icon name={getTypeIcon(conversation?.type)} size={20} color="white" />
                    </div>
                  )}
                  {conversation?.type === 'direct' && (
                    <div className="absolute -bottom-1 -right-1">
                      {getOnlineStatus(conversation?.isOnline)}
                    </div>
                  )}
                  {conversation?.unreadCount > 0 && (
                    <div className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {conversation?.unreadCount > 9 ? '9+' : conversation?.unreadCount}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-medium text-foreground truncate">{conversation?.name}</h4>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {formatTime(conversation?.lastMessageTime)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {conversation?.type !== 'direct' && (
                      <Icon name={getTypeIcon(conversation?.type)} size={12} className="text-muted-foreground" />
                    )}
                    <p className={`text-sm truncate ${
                      conversation?.unreadCount > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'
                    }`}>
                      {conversation?.lastMessage}
                    </p>
                  </div>
                </div>

                {/* Status Indicators */}
                <div className="flex flex-col items-end space-y-1">
                  {conversation?.isPinned && (
                    <Icon name="Pin" size={14} className="text-primary" />
                  )}
                  {conversation?.isMuted && (
                    <Icon name="VolumeX" size={14} className="text-muted-foreground" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationList;